import { useState } from "react";
import { Link } from "react-router-dom";
import { Check, ChevronLeft, ChevronRight, Paperclip } from "lucide-react";
import { useTranslation } from "@/shared/providers/LanguageProvider";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { FormField, inputClass, selectClass, textareaClass } from "../components/FormField";
import { CurrencyAmountInput, formatInvoiceAmount, InvoiceCurrencyFields, isValidExchangeRateCdf } from "../components/InvoiceCurrencyFields";
import { useAdminInvoiceSubmit } from "../hooks/useAdminInvoiceSubmit";

function formatDisplayDate(value: string) {
  if (!value) return "—";
  const [year, month, day] = value.split("-");
  return `${day}/${month}/${year}`;
}

type AdminSubmitInvoiceWizardProps = {
  invoiceId?: number;
};

/**
 * Admin : nouvelle facture (cahier des charges, lot 3). Assureur*, hôpital* (réseau de l'assureur), entreprise*
 * (ou toutes ses entreprises), référence de la facture du prestataire*, période*, n° du lot de dépôt*, PDF*,
 * montant nominal* (le total donné par l'hôpital) et montant synthétique facultatif. L'échéance est fixée à la
 * création selon les délais de traitement de l'assureur.
 */
export function AdminSubmitInvoiceWizard({ invoiceId }: AdminSubmitInvoiceWizardProps = {}) {
  const { t } = useTranslation();
  const labels = t.safecheck.invoices.submit;
  const [step, setStep] = useState<0 | 1>(0);
  const [fileLabel, setFileLabel] = useState(labels.fields.noFileSelected);
  const {
    form,
    updateForm,
    insurers,
    hospitals,
    hospitalName,
    companies,
    selectedCompany,
    isEditMode,
    existingFileName,
    loadingMeta,
    loadingEdit,
    loadingCompanies,
    submitting,
    error,
    submittedInvoiceId,
    submit,
  } = useAdminInvoiceSubmit(invoiceId);

  const hasExistingFile = Boolean(existingFileName);
  const insurerName = insurers.find((insurer) => String(insurer.id) === form.insurerId)?.name ?? "—";
  const currencyOk = form.invoiceCurrency !== "CDF" || isValidExchangeRateCdf(form.exchangeRateCdf);

  const issues: string[] = [];
  if (!form.insurerId) issues.push("Choisissez l'assureur.");
  if (!form.hospitalId) issues.push("Choisissez l'hôpital.");
  if (!form.companyId) issues.push("Choisissez l'entreprise.");
  if (!form.providerReference.trim()) issues.push("Indiquez la référence de la facture du prestataire.");
  if (!form.periodFrom || !form.periodTo) issues.push("Indiquez la période facturée.");
  else if (form.periodTo < form.periodFrom) issues.push("La fin de la période doit suivre son début.");
  if (!form.batchNumber.trim()) issues.push("Indiquez le n° du lot de dépôt.");
  if (!form.receivedAt) issues.push("Indiquez la date de réception de la facture.");
  else if (form.receivedAt > new Date().toISOString().slice(0, 10)) issues.push("La date de réception ne peut pas être dans le futur.");
  if (!form.file && !hasExistingFile) issues.push("Joignez la facture (PDF).");
  if (!(Number(form.totalAmount) > 0)) issues.push("Indiquez le montant nominal (total donné par l'hôpital).");
  if (!currencyOk) issues.push("Taux FC/USD invalide.");

  const currencyFieldLabels = {
    currency: labels.fields.currency,
    currencyHint: labels.fields.currencyHint,
    exchangeRate: labels.fields.exchangeRate,
    exchangeRateHint: labels.fields.exchangeRateHint,
    currencyUsd: labels.fields.currencyUsd,
    currencyCdf: labels.fields.currencyCdf,
  };

  const breadcrumbs = (
    <Breadcrumbs
      homeHref="/dashboard/admin"
      items={[{ label: t.safecheck.invoices.billing }, { label: isEditMode ? "Modifier la facture" : "Nouvelle facture" }]}
    />
  );

  if (submittedInvoiceId) {
    return (
      <>
        {breadcrumbs}
        <div className="theme-card p-6">
          <div className="rounded-xl border border-green/30 bg-green/10 px-4 py-3 text-sm text-foreground">
            {isEditMode ? `Facture #${submittedInvoiceId} mise à jour.` : `Facture enregistrée (#${submittedInvoiceId}) : son échéance est fixée selon les délais de l'assureur.`}
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <Link to={`/dashboard/invoices/${submittedInvoiceId}`} className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-accent hover:text-accent-foreground">
              Voir la facture
            </Link>
            <Link to="/dashboard/invoices" className="inline-flex items-center gap-2 rounded-lg border border-border px-4 py-2 text-sm text-foreground hover:bg-accent/20 hover:border-accent">
              Liste des factures
            </Link>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      {breadcrumbs}

      <div className="theme-card">
        <div className="border-b border-border px-6 py-5">
          <h1 className="text-lg font-semibold text-foreground">{isEditMode ? `Modifier la facture #${invoiceId}` : "Nouvelle facture"}</h1>
          <p className="mt-1 text-xs text-muted">
            Les champs marqués * sont obligatoires.
            {loadingMeta || loadingEdit ? " Chargement…" : ""}
          </p>
          {error ? <p className="mt-2 text-sm text-pink">{error}</p> : null}
        </div>

        {step === 0 ? (
          <div className="space-y-5 p-6">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <FormField label="Assureur" required>
                <select
                  value={form.insurerId}
                  onChange={(e) => {
                    updateForm("insurerId", e.target.value);
                    updateForm("hospitalId", "");
                    updateForm("companyId", "");
                  }}
                  className={selectClass}
                >
                  <option value="">Sélectionner l'assureur</option>
                  {insurers.map((insurer) => (
                    <option key={insurer.id} value={String(insurer.id)}>{insurer.name}</option>
                  ))}
                </select>
              </FormField>
              <FormField label="Hôpital" required hint={form.insurerId ? "Prestataires du réseau de l'assureur." : undefined}>
                <select
                  value={form.hospitalId}
                  onChange={(e) => {
                    updateForm("hospitalId", e.target.value);
                    updateForm("companyId", "");
                  }}
                  className={selectClass}
                  disabled={!form.insurerId}
                >
                  <option value="">{form.insurerId ? "Sélectionner l'hôpital" : "Choisissez d'abord l'assureur"}</option>
                  {hospitals.map((h) => (
                    <option key={h.id} value={String(h.id)}>{h.name}</option>
                  ))}
                </select>
              </FormField>
              <FormField label="Entreprise" required>
                <select
                  value={form.companyId}
                  onChange={(e) => updateForm("companyId", e.target.value)}
                  className={selectClass}
                  disabled={!form.hospitalId || loadingCompanies}
                >
                  <option value="">{form.hospitalId ? "Sélectionner l'entreprise" : "Choisissez d'abord l'hôpital"}</option>
                  {companies.map((c) => (
                    <option key={c.id} value={String(c.id)}>{c.name}</option>
                  ))}
                </select>
              </FormField>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
              <FormField label="Réf. facture prestataire" required>
                <input value={form.providerReference} onChange={(e) => updateForm("providerReference", e.target.value)} className={inputClass} placeholder="CTK-2026-10-001" />
              </FormField>
              <FormField label="Période facturée du" required>
                <input type="date" value={form.periodFrom} onChange={(e) => updateForm("periodFrom", e.target.value)} className={inputClass} />
              </FormField>
              <FormField label="Au" required>
                <input type="date" value={form.periodTo} onChange={(e) => updateForm("periodTo", e.target.value)} className={inputClass} />
              </FormField>
              <FormField label="Lot de dépôt n°" required>
                <input value={form.batchNumber} onChange={(e) => updateForm("batchNumber", e.target.value)} className={inputClass} placeholder="LOT-2026-10-01" />
              </FormField>
              <FormField label="Date de réception" required hint="Arrivée de la facture chez SafeCheck : le délai convenu court à partir de cette date.">
                <input type="date" value={form.receivedAt} max={new Date().toISOString().slice(0, 10)} onChange={(e) => updateForm("receivedAt", e.target.value)} className={inputClass} />
              </FormField>
            </div>

            <FormField label="Facture (PDF)" required={!hasExistingFile}>
              <label className="flex cursor-pointer items-center gap-3 rounded-lg border border-dashed border-border px-4 py-3 hover:bg-accent/20 hover:border-accent">
                <Paperclip size={16} className="text-muted" />
                <span className="text-sm text-foreground">
                  {fileLabel}
                  {hasExistingFile && !form.file ? ` (actuel : ${existingFileName})` : ""}
                </span>
                <input
                  type="file"
                  accept=".pdf,.png,.jpg,.jpeg"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0] ?? null;
                    updateForm("file", file);
                    setFileLabel(file?.name ?? labels.fields.noFileSelected);
                  }}
                />
              </label>
              <p className="mt-1.5 text-xs text-muted">{labels.fields.attachmentHint}</p>
            </FormField>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <FormField label="Montant nominal" required hint="Le total donné par l'hôpital.">
                <CurrencyAmountInput value={form.totalAmount} onChange={(value) => updateForm("totalAmount", value)} currency={form.invoiceCurrency} />
              </FormField>
              <FormField label="Montant synthétique (facultatif)" hint="Total de la facture synthétique, comparé par le Sinistre.">
                <CurrencyAmountInput value={form.totalSynthetique} onChange={(value) => updateForm("totalSynthetique", value)} currency={form.invoiceCurrency} />
              </FormField>
            </div>

            {!isEditMode ? (
              <FormField label="Facture synthétique (facultatif)">
                <input
                  id="invoice-file-synthetique"
                  type="file"
                  accept=".pdf,.png,.jpg,.jpeg"
                  className="text-sm"
                  onChange={(e) => updateForm("fileSynthetique", e.target.files?.[0] ?? null)}
                />
              </FormField>
            ) : null}

            <InvoiceCurrencyFields
              currency={form.invoiceCurrency}
              exchangeRateCdf={form.exchangeRateCdf}
              onCurrencyChange={(value) => {
                updateForm("invoiceCurrency", value);
                if (value === "USD") updateForm("exchangeRateCdf", "");
              }}
              onExchangeRateChange={(value) => updateForm("exchangeRateCdf", value)}
              labels={currencyFieldLabels}
            />

            <FormField label={labels.fields.notes}>
              <textarea value={form.notes} onChange={(e) => updateForm("notes", e.target.value)} className={textareaClass} rows={2} />
            </FormField>
          </div>
        ) : (
          <div className="p-6">
            <div className="rounded-xl border border-border bg-card-muted/30 p-4 text-sm leading-7">
              <p><strong>Assureur :</strong> {insurerName}</p>
              <p><strong>Hôpital :</strong> {hospitalName}</p>
              <p><strong>Entreprise :</strong> {selectedCompany?.name ?? "—"}</p>
              <p><strong>Réf. facture prestataire :</strong> {form.providerReference}</p>
              <p><strong>Période :</strong> {formatDisplayDate(form.periodFrom)} → {formatDisplayDate(form.periodTo)}</p>
              <p><strong>Lot de dépôt :</strong> {form.batchNumber}</p>
              <p><strong>Reçue le :</strong> {formatDisplayDate(form.receivedAt)}</p>
              <p><strong>Montant nominal :</strong> {formatInvoiceAmount(form.totalAmount, form.invoiceCurrency)}</p>
              {form.totalSynthetique.trim() ? (
                <p><strong>Montant synthétique :</strong> {formatInvoiceAmount(form.totalSynthetique, form.invoiceCurrency)}</p>
              ) : null}
              <p><strong>Facture :</strong> {form.file?.name ?? existingFileName ?? "—"}</p>
            </div>
          </div>
        )}

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border px-6 py-4">
          {step === 0 && issues.length > 0 ? <p className="w-full text-sm text-pink">{issues.join(" ")}</p> : null}
          <button
            type="button"
            disabled={step === 0}
            onClick={() => setStep(0)}
            className="inline-flex items-center gap-2 rounded-lg border border-border px-4 py-2 text-sm disabled:opacity-40"
          >
            <ChevronLeft size={16} /> {labels.previous}
          </button>
          {step === 0 ? (
            <button
              type="button"
              disabled={issues.length > 0}
              onClick={() => setStep(1)}
              className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white disabled:opacity-40"
            >
              Récapitulatif <ChevronRight size={16} />
            </button>
          ) : (
            <button
              type="button"
              disabled={submitting}
              onClick={() => void submit()}
              className="inline-flex items-center gap-2 rounded-lg bg-green px-4 py-2 text-sm font-medium text-white disabled:opacity-60"
            >
              <Check size={16} /> {submitting ? "…" : isEditMode ? "Enregistrer" : "Enregistrer la facture"}
            </button>
          )}
        </div>
      </div>
    </>
  );
}
